import { Controller } from "@hotwired/stimulus"

const FIELD_TYPE_LABELS = {
  text: "Short Text",
  textarea: "Title",
  select: "Single Choice",
  multi_select: "Multiple Choice",
  rating: "Rating",
  number: "Number",
  email: "Email",
  phone: "Phone",
  date: "Date",
  checkbox: "Checkbox",
  yes_no: "Yes / No",
  opinion_scale: "Opinion Scale",
  file_upload: "File Upload",
  url: "Website",
  ranking: "Ranking"
}

export default class extends Controller {
  static targets = [
    "fieldList", "fieldCount", "emptyState", "formTypeSelector",
    "showResultsSetting", "anonymousSetting", "progressBarSetting", "settingsSection"
  ]

  static values = {
    activityId: String,
    inloopId: String,
    formType: String,
    saveUrl: String,
    renderFieldUrl: String,
    readOnly: Boolean
  }

  connect() {
    this.saveTimeout = null
    this.updateFieldCount()
  }

  disconnect() {
    if (this.saveTimeout) clearTimeout(this.saveTimeout)
  }

  // ==================
  // Form Type
  // ==================

  selectFormType(e) {
    if (this.readOnlyValue) return
    const select = e.currentTarget.tagName === "SELECT" ? e.currentTarget : e.currentTarget.querySelector("select")
    const type = select ? select.value : e.currentTarget.value
    if (!type) return
    this.formTypeValue = type

    if (this.hasSettingsSectionTarget) {
      this.settingsSectionTarget.style.display = type === "multi_step" ? "" : "none"
    }

    // Toggle content type options for polling
    this.element.querySelectorAll("[data-form-only]").forEach(el => {
      el.classList.toggle("hidden", type === "polling")
    })

    // Update step badges visibility
    this.fieldListTarget.querySelectorAll("[data-step-badge]").forEach((badge, idx) => {
      badge.style.display = type === "multi_step" ? "" : "none"
    })

    this.scheduleSave()
  }

  // ==================
  // Settings
  // ==================

  updateSettings() {
    this.scheduleSave()
  }

  // ==================
  // Field CRUD
  // ==================

  async addField(e) {
    if (this.readOnlyValue) return
    const type = e.currentTarget.dataset.fieldType
    const fieldCount = this.fieldListTarget.querySelectorAll(":scope > [data-field-id]").length
    const isMultiStep = this.formTypeValue === "multi_step"

    const newField = this.buildDefaultField(type, fieldCount)
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(this.renderFieldUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
          "Accept": "text/html"
        },
        body: JSON.stringify({
          field: newField,
          index: fieldCount,
          is_multi_step: isMultiStep,
          read_only: false
        })
      })

      if (response.ok) {
        const html = await response.text()
        this.fieldListTarget.insertAdjacentHTML("beforeend", html)
        this.updateFieldCount()
        this.scheduleSave()
      }
    } catch (err) {
      console.error("Error adding field:", err)
    }
  }

  removeField(e) {
    if (this.readOnlyValue) return
    const fieldId = e.currentTarget.dataset.fieldId
    const fieldEl = this.fieldListTarget.querySelector(`[data-field-id="${fieldId}"]`)
    if (fieldEl) {
      fieldEl.remove()
      this.renumberFields()
      this.updateFieldCount()
      this.scheduleSave()
    }
  }

  toggleRequired(e) {
    if (this.readOnlyValue) return
    const fieldId = e.currentTarget.dataset.fieldId
    const fieldEl = this.fieldListTarget.querySelector(`[data-field-id="${fieldId}"]`)
    if (!fieldEl) return

    const isNowRequired = fieldEl.dataset.fieldRequired !== "true"
    fieldEl.dataset.fieldRequired = isNowRequired

    const btn = e.currentTarget
    btn.textContent = isNowRequired ? "Required" : "Optional"
    btn.className = `px-2 py-0.5 text-xs rounded ${isNowRequired ? "bg-red-50 text-red-600 font-medium" : "text-gray-400 hover:text-gray-600"}`

    this.scheduleSave()
  }

  updateFieldLabel(e) {
    this.scheduleSave()
  }

  updateFieldDescription(e) {
    this.scheduleSave()
  }

  // ==================
  // Options (select/multi_select/yes_no/ranking)
  // ==================

  addOption(e) {
    if (this.readOnlyValue) return
    const fieldId = e.currentTarget.dataset.fieldId
    const container = this.fieldListTarget.querySelector(`[data-options-container][data-field-id="${fieldId}"]`)
    if (!container) return

    const existingOptions = container.querySelectorAll("[data-option-id]")
    const idx = existingOptions.length
    const optId = `opt_${idx}`

    const letter = String.fromCharCode(65 + idx)
    const optionHtml = `
      <div class="flex items-center gap-2 py-1 pl-3 pr-3 rounded-lg border border-gray-200" data-option-id="${optId}">
        <span class="w-4 h-4 flex items-center justify-center rounded text-xs font-bold border border-gray-300 text-gray-500 flex-shrink-0" style="font-size: 10px;">${letter}</span>
        <input type="text" value="Option ${idx + 1}" data-field-id="${fieldId}" data-option-id="${optId}"
               data-action="change->form-builder#updateOptionLabel"
               class="flex-1 text-sm border-0 bg-transparent focus:ring-0 px-0 py-0 text-gray-600 placeholder-gray-300"
               placeholder="Option label">
        <button type="button" data-action="click->form-builder#removeOption"
                data-field-id="${fieldId}" data-option-id="${optId}"
                class="p-0.5 text-gray-300 hover:text-error-400 transition-colors">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
        </button>
      </div>
    `

    e.currentTarget.insertAdjacentHTML("beforebegin", optionHtml)
    this.scheduleSave()
  }

  removeOption(e) {
    if (this.readOnlyValue) return
    const optionEl = e.currentTarget.closest("[data-option-id]")
    if (!optionEl) return

    const container = optionEl.closest("[data-options-container]")
    optionEl.remove()

    // Re-letter remaining options (A, B, C...)
    if (container) {
      container.querySelectorAll("[data-option-id]").forEach((el, i) => {
        const badge = el.querySelector("span")
        if (badge) badge.textContent = String.fromCharCode(65 + i)
      })
    }

    this.scheduleSave()
  }

  updateOptionLabel(e) {
    this.scheduleSave()
  }

  // ==================
  // DOM State Collection
  // ==================

  collectFormData() {
    const fields = []
    this.fieldListTarget.querySelectorAll(":scope > [data-field-id]").forEach((el, idx) => {
      const titleInput = el.querySelector('input[data-action*="updateFieldLabel"]')
      const descInput = el.querySelector('input[data-action*="updateFieldDescription"]')

      const field = {
        id: el.dataset.fieldId,
        type: el.dataset.fieldType,
        label: titleInput?.value || "",
        description: descInput?.value || "",
        required: el.dataset.fieldRequired === "true",
        position: idx
      }

      // Collect options (skip empty labels)
      const optionsContainer = el.querySelector("[data-options-container]")
      if (optionsContainer) {
        field.options = []
        optionsContainer.querySelectorAll("[data-option-id]").forEach(optEl => {
          const optInput = optEl.querySelector("input")
          const label = (optInput?.value || "").trim()
          if (!label) return
          field.options.push({
            id: optEl.dataset.optionId,
            label: label,
            value: label.toLowerCase().replace(/[^a-z0-9]+/g, "_")
          })
        })
      }

      // Yes/No fixed options
      if (field.type === "yes_no") {
        field.options = [
          { id: "opt_0", label: "Yes", value: "yes" },
          { id: "opt_1", label: "No", value: "no" }
        ]
      }

      // Opinion scale validation
      if (field.type === "opinion_scale") {
        field.validation = { min_value: 1, max_value: 10 }
      }

      fields.push(field)
    })

    const formType = this.formTypeValue || "rich_form"

    // Auto-generate steps for multi_step
    const steps = formType === "multi_step"
      ? fields.map((f, i) => ({ id: i + 1, title: f.label || `Step ${i + 1}`, description: "" }))
      : []

    if (formType === "multi_step") {
      fields.forEach((f, i) => { f.step = i + 1 })
    }

    // Collect settings
    const settings = {}
    if (this.hasProgressBarSettingTarget) {
      settings.show_progress_bar = this.progressBarSettingTarget.querySelector("input[type=checkbox]")?.checked ?? true
    }
    if (this.hasShowResultsSettingTarget) {
      settings.show_results_after_submit = this.showResultsSettingTarget.checked
    }
    if (this.hasAnonymousSettingTarget) {
      settings.anonymous_responses = this.anonymousSettingTarget.checked
    }

    return { form_type: formType, fields, steps, settings }
  }

  // ==================
  // Save
  // ==================

  scheduleSave() {
    if (this.readOnlyValue) return
    if (this.saveTimeout) clearTimeout(this.saveTimeout)
    this.saveTimeout = setTimeout(() => this.save(), 800)
  }

  async save() {
    try {
      const formData = this.collectFormData()
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(this.saveUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": token,
          "Accept": "application/json"
        },
        body: JSON.stringify({ form_data: formData })
      })

      if (!response.ok) {
        console.error("Failed to save form config:", await response.text())
      }
    } catch (err) {
      console.error("Error saving form config:", err)
    }
  }

  // ==================
  // Helpers
  // ==================

  buildDefaultField(type, position) {
    const field = {
      id: `field_${Math.random().toString(36).substr(2, 12)}`,
      type,
      label: FIELD_TYPE_LABELS[type] || "New Field",
      description: "",
      required: false,
      position
    }

    if (["select", "multi_select"].includes(type)) {
      field.options = [
        { id: "opt_0", label: "Option 1", value: "option_1" },
        { id: "opt_1", label: "Option 2", value: "option_2" },
        { id: "opt_2", label: "Option 3", value: "option_3" }
      ]
    } else if (type === "yes_no") {
      field.options = [
        { id: "opt_0", label: "Yes", value: "yes" },
        { id: "opt_1", label: "No", value: "no" }
      ]
    } else if (type === "ranking") {
      field.options = [
        { id: "opt_0", label: "Item 1", value: "item_1" },
        { id: "opt_1", label: "Item 2", value: "item_2" },
        { id: "opt_2", label: "Item 3", value: "item_3" }
      ]
    }

    if (type === "opinion_scale") {
      field.validation = { min_value: 1, max_value: 10 }
    }

    return field
  }

  updateFieldCount() {
    const count = this.fieldListTarget.querySelectorAll(":scope > [data-field-id]").length
    if (this.hasFieldCountTarget) {
      this.fieldCountTarget.textContent = `${count} item(s)`
    }
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.toggle("hidden", count > 0)
    }
  }

  renumberFields() {
    this.fieldListTarget.querySelectorAll(":scope > [data-field-id]").forEach((el, idx) => {
      const numberBadge = el.querySelector("[data-field-number]")
      if (numberBadge) numberBadge.textContent = idx + 1

      const stepBadge = el.querySelector("[data-step-badge]")
      if (stepBadge) stepBadge.textContent = `Step ${idx + 1}`
    })
  }
}
